import React, { useEffect, useRef, useState } from "react"
import { TAgetUsersByCategories, TAbrandUserCategories } from "../../../services/userAPI"
import StickyDataTable from "../../../components/StickyDataTable"
import ProfileDetailModal from "../../../components/ProfileDetailModal"
import { numFixer } from "../getAllUsers/components/Functions"
import { isAbortError } from "../../../types/error"
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import {
  faSearch,
  faSpinner,
  faTimes,
  faChevronDown,
  faChevronUp,
  faFilter,
  faEye,
} from "@fortawesome/free-solid-svg-icons"

interface SubCategoryOption {
  name?: string
  category?: string
  count: number
}

interface CategoryGroup {
  main_category: string
  sub_categories: SubCategoryOption[]
}

interface FlatSubCategory {
  category: string
  confidence: number
}

interface CategoryUser {
  _id: string
  name: string
  phone: string
  country: string
  city: string
  gender: string
  age: number
  job: string
  hobbies: string[]
  category: string
  sub_category: string
  flat_sub_categories: FlatSubCategory[]
  sub_confidence: number
  metrics_status: boolean
  insta?: {
    username: string
    full_name: string
    followers: number
    engagement_rate: number
    reels_engagement_rate: number
    average_like: number
    likes_for_price: number
    reels_views_for_price: number
    profile_pic: string
    biography: string
    chatgpt_analysis_v5: any[]
    segmentation: any
  }
  tiktok?: {
    username: string
    tiktok_nickname: string
    followers: number
    tiktok_engagement_rate: number
    tiktok_average_like: number
    profile_pic: string
    bio_description: string
    chatgpt_analysis_v5: any[]
    segmentation: any
  }
}

const UsersByCategories = () => {
  const abortControllerRef = useRef<AbortController | null>(null)
  const dropdownRef = useRef<HTMLDivElement>(null)

  // Category data from API
  const [categoryGroups, setCategoryGroups] = useState<CategoryGroup[]>([])
  const [selectedCategories, setSelectedCategories] = useState<string[]>([])
  const [categorySearch, setCategorySearch] = useState("")
  const [isCategoryDropdownOpen, setIsCategoryDropdownOpen] = useState(false)
  const [expandedGroups, setExpandedGroups] = useState<string[]>([])

  // Main filters
  const [platform, setPlatform] = useState("insta")
  const [confidence, setConfidence] = useState(0.85)
  const [country, setCountry] = useState("")
  const [gender, setGender] = useState("")
  const [username, setUsername] = useState("")

  // Range filters
  const [minAge, setMinAge] = useState("")
  const [maxAge, setMaxAge] = useState("")
  const [minFollowers, setMinFollowers] = useState("")
  const [maxFollowers, setMaxFollowers] = useState("")
  const [minEngagement, setMinEngagement] = useState("")
  const [maxEngagement, setMaxEngagement] = useState("")
  const [minAvgLike, setMinAvgLike] = useState("")
  const [maxAvgLike, setMaxAvgLike] = useState("")
  const [minAvgView, setMinAvgView] = useState("")
  const [maxAvgView, setMaxAvgView] = useState("")

  // Advanced filter toggle
  const [showAdvanced, setShowAdvanced] = useState(false)

  // Sorting
  const [sortByColumn, setSortByColumn] = useState("")
  const [sortOrder, setSortOrder] = useState(false)

  // Table state
  const [page, setPage] = useState(1)
  const PAGE_SIZES = [10, 20, 30, 50, 100]
  const [pageSize, setPageSize] = useState(PAGE_SIZES[1])
  const [totalRecords, setTotalRecords] = useState(0)
  const [records, setRecords] = useState<CategoryUser[]>([])
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const [searched, setSearched] = useState(false)

  const [selectedUser, setSelectedUser] = useState<any>(null)
  const [isOpenDetailModal, setIsOpenDetailModal] = useState(false)

  // Fetch categories on mount
  useEffect(() => {
    const fetchCategories = async () => {
      try {
        const response = await TAbrandUserCategories()
        if (response && response.data) {
          setCategoryGroups(response.data)
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.log("Error fetching categories:", err)
      }
    }
    fetchCategories()
  }, [])

  // Close dropdown on outside click
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
        setIsCategoryDropdownOpen(false)
      }
    }
    document.addEventListener("mousedown", handleClickOutside)
    return () => document.removeEventListener("mousedown", handleClickOutside)
  }, [])

  useEffect(() => {
    return () => {
      if (abortControllerRef.current) {
        abortControllerRef.current.abort()
      }
    }
  }, [])

  const getSubCategoryName = (sub: SubCategoryOption) => sub.name || sub.category || ""

  const toggleCategory = (categoryName: string) => {
    setSelectedCategories((prev) => {
      if (prev.includes(categoryName)) {
        return prev.filter((c) => c !== categoryName)
      }
      return [...prev, categoryName]
    })
  }

  const toggleMainCategory = (mainCategory: string) => {
    const group = categoryGroups.find((g) => g.main_category === mainCategory)
    if (!group) {
      return
    }
    const subNames = group.sub_categories.map(getSubCategoryName).filter(Boolean)
    const allSelected = subNames.every((name) => selectedCategories.includes(name))

    if (allSelected) {
      setSelectedCategories((prev) => prev.filter((c) => !subNames.includes(c)))
    } else {
      setSelectedCategories((prev) => {
        const newList = [...prev]
        subNames.forEach((name) => {
          if (!newList.includes(name)) {
            newList.push(name)
          }
        })
        return newList
      })
    }
  }

  const toggleGroupExpand = (mainCategory: string) => {
    setExpandedGroups((prev) => {
      if (prev.includes(mainCategory)) {
        return prev.filter((g) => g !== mainCategory)
      }
      return [...prev, mainCategory]
    })
  }

  const removeCategory = (categoryName: string) => {
    setSelectedCategories((prev) => prev.filter((c) => c !== categoryName))
  }

  const clearAllCategories = () => {
    setSelectedCategories([])
  }

  // Filter category groups by search term
  const filteredGroups = categoryGroups
    .map((group) => {
      const searchLower = categorySearch.toLowerCase()
      const matchMain = group.main_category.toLowerCase().includes(searchLower)
      const filteredSubs = group.sub_categories.filter((sub) => {
        const name = getSubCategoryName(sub).toLowerCase()
        return matchMain || name.includes(searchLower)
      })
      return { ...group, sub_categories: filteredSubs }
    })
    .filter((group) => group.sub_categories.length > 0)

  const buildParams = (currentPage?: number) => {
    const params = new URLSearchParams()
    params.set("categories", selectedCategories.join(","))
    params.set("page", (currentPage || page).toString())
    params.set("perPage", pageSize.toString())
    params.set("platform", platform)
    params.set("confidence", confidence.toString())

    if (country) { params.set("country", country) }
    if (gender) { params.set("gender", gender) }
    if (username) { params.set("username", username) }

    if (minAge) { params.set("min_age", minAge) }
    if (maxAge) { params.set("max_age", maxAge) }
    if (minFollowers) { params.set("min_followers", minFollowers) }
    if (maxFollowers) { params.set("max_followers", maxFollowers) }
    if (minEngagement) { params.set("min_engagement_rate", minEngagement) }
    if (maxEngagement) { params.set("max_engagement_rate", maxEngagement) }
    if (minAvgLike) { params.set("min_average_like", minAvgLike) }
    if (maxAvgLike) { params.set("max_average_like", maxAvgLike) }
    if (minAvgView) { params.set("min_average_view", minAvgView) }
    if (maxAvgView) { params.set("max_average_view", maxAvgView) }

    if (sortByColumn) {
      params.set("sortingType", sortByColumn)
      params.set("sortingIndex", sortOrder ? "increase" : "decrease")
    }

    return params.toString()
  }

  const fetchData = async (currentPage?: number) => {
    if (selectedCategories.length === 0) {
      setError("Lütfen en az bir kategori seçin.")
      return
    }

    if (abortControllerRef.current) {
      abortControllerRef.current.abort()
    }
    abortControllerRef.current = new AbortController()

    setLoading(true)
    setError(null)
    setSearched(true)

    try {
      const response = await TAgetUsersByCategories(
        buildParams(currentPage),
        abortControllerRef.current.signal,
      )

      if (response) {
        setRecords(response.users || [])
        setTotalRecords(response.totalDocuments || 0)
      }
    } catch (err: any) {
      if (!isAbortError(err)) {
        setError(err?.message || "Bir hata oluştu.")
      }
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (searched) {
      fetchData()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, pageSize])

  const handleSearch = () => {
    setPage(1)
    fetchData(1)
  }

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === "Enter") {
      handleSearch()
    }
  }

  const platformKey = platform.startsWith("tiktok") ? "tiktok" : "insta"

  const columns = [
    {
      accessor: "id",
      title: "#",
      sortable: false,
    },
    {
      accessor: "details",
      title: "Detaylar",
      sortable: false,
      render: (record: CategoryUser) => (
        <a href={`/user/find/${record._id}`} target="_blank" rel="noopener noreferrer">
          <div className="text-center items-center">
            <FontAwesomeIcon icon={faEye} style={{ color: "#005eff" }} />
          </div>
        </a>
      ),
    },
    {
      accessor: "name",
      title: "İsim",
      sortable: false,
      render: (record: CategoryUser) => (
        <button
          className="text-gray-900 hover:underline font-medium"
          onClick={() => {
            setSelectedUser(record)
            setIsOpenDetailModal(true)
          }}
        >
          {record.name || "-"}
        </button>
      ),
    },
    {
      accessor: "username",
      title: platformKey === "insta" ? "Instagram" : "TikTok",
      sortable: false,
      render: (record: CategoryUser) => {
        const data = record[platformKey]
        const uname =
          platformKey === "tiktok"
            ? (data as CategoryUser["tiktok"])?.username
            : (data as CategoryUser["insta"])?.username
        if (!uname) { return "-" }
        const url =
          platformKey === "insta"
            ? `https://instagram.com/${uname}`
            : `https://tiktok.com/@${uname}`
        return (
          <a
            href={url}
            target="_blank"
            rel="noopener noreferrer"
            className="text-gray-900 hover:underline"
          >
            @{uname}
          </a>
        )
      },
    },
    {
      accessor: "followers",
      title: "Takipçi",
      sortable: true,
      render: (record: CategoryUser) => {
        const data = record[platformKey]
        const followers =
          platformKey === "tiktok"
            ? (data as CategoryUser["tiktok"])?.followers
            : (data as CategoryUser["insta"])?.followers
        return followers?.toLocaleString("tr-TR") || "-"
      },
    },
    {
      accessor: "engagement_rate",
      title: "Etkileşim Oranı",
      sortable: true,
      render: (record: CategoryUser) => {
        const data = record[platformKey]
        const rate =
          platformKey === "tiktok"
            ? (data as CategoryUser["tiktok"])?.tiktok_engagement_rate
            : (data as CategoryUser["insta"])?.engagement_rate
        return numFixer(rate || 0)
      },
    },
    {
      accessor: "average_like",
      title: "Ort. Beğeni",
      sortable: true,
      render: (record: CategoryUser) => {
        const data = record[platformKey]
        const avgLike =
          platformKey === "tiktok"
            ? (data as CategoryUser["tiktok"])?.tiktok_average_like
            : (data as CategoryUser["insta"])?.average_like
        return numFixer(avgLike || 0)
      },
    },
    {
      accessor: "country",
      title: "Ülke",
      sortable: false,
      render: (record: CategoryUser) => record.country || "-",
    },
    {
      accessor: "gender",
      title: "Cinsiyet",
      sortable: false,
      render: (record: CategoryUser) => record.gender || "-",
    },
    {
      accessor: "age",
      title: "Yaş",
      sortable: true,
      render: (record: CategoryUser) => record.age || "-",
    },
    {
      accessor: "sub_confidence",
      title: "Kategori Güveni",
      sortable: false,
      render: (record: CategoryUser) => {
        const val = record.sub_confidence
        if (!val) { return "-" }
        const pct = (val * 100).toFixed(1)
        const color =
          val >= 0.95
            ? "text-green-700 bg-green-100"
            : val >= 0.85
              ? "text-blue-700 bg-blue-100"
              : "text-yellow-700 bg-yellow-100"
        return (
          <span
            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold ${color}`}
          >
            %{pct}
          </span>
        )
      },
    },
    {
      accessor: "flat_sub_categories",
      title: "Eşleşen Kategoriler",
      sortable: false,
      render: (record: CategoryUser) => {
        const matched = record.flat_sub_categories?.filter(
          (sub) => selectedCategories.includes(sub.category) && sub.confidence >= confidence,
        )
        if (!matched || matched.length === 0) { return "-" }
        return (
          <div className="flex flex-wrap gap-1">
            {matched.map((cat, i) => (
              <span
                key={i}
                className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
              >
                {cat.category}
                <span className="ml-1 text-indigo-500">
                  ({(cat.confidence * 100).toFixed(0)}%)
                </span>
              </span>
            ))}
          </div>
        )
      },
    },
  ]

  const RangeInput = ({
    label,
    minVal,
    maxVal,
    setMin,
    setMax,
  }: {
    label: string
    minVal: string
    maxVal: string
    setMin: (v: string) => void
    setMax: (v: string) => void
  }) => (
    <div>
      <label className="block text-xs font-medium text-gray-600 mb-1">{label}</label>
      <div className="flex gap-2">
        <input
          type="number"
          value={minVal}
          onChange={(e) => setMin(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Min"
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
        />
        <input
          type="number"
          value={maxVal}
          onChange={(e) => setMax(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Max"
          className="w-full px-2 py-1.5 border border-gray-300 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
        />
      </div>
    </div>
  )

  return (
    <div className="panel px-4 py-6">
      <h2 className="text-2xl font-bold text-gray-800 mb-6">Kategoriye Göre Kullanıcılar</h2>

      {/* Filter Section */}
      <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-5 mb-6">
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Categories Multi-Select Dropdown */}
          <div className="lg:col-span-2" ref={dropdownRef}>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Kategoriler <span className="text-red-500">*</span>
            </label>

            {/* Selected categories tags */}
            {selectedCategories.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mb-2">
                {selectedCategories.map((cat) => (
                  <span
                    key={cat}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium bg-indigo-100 text-indigo-800"
                  >
                    {cat}
                    <button
                      type="button"
                      onClick={() => removeCategory(cat)}
                      className="hover:text-indigo-600"
                    >
                      <FontAwesomeIcon icon={faTimes} className="w-2.5 h-2.5" />
                    </button>
                  </span>
                ))}
                <button
                  type="button"
                  onClick={clearAllCategories}
                  className="text-xs text-red-500 hover:text-red-700 ml-1"
                >
                  Tümünü Temizle
                </button>
              </div>
            )}

            {/* Dropdown trigger */}
            <div className="relative">
              <button
                type="button"
                onClick={() => setIsCategoryDropdownOpen(!isCategoryDropdownOpen)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm text-left focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition bg-white flex items-center justify-between"
              >
                <span className="text-gray-500">
                  {selectedCategories.length === 0
                    ? "Kategori seçin..."
                    : `${selectedCategories.length} kategori seçildi`}
                </span>
                <FontAwesomeIcon
                  icon={faChevronDown}
                  className={`w-3 h-3 text-gray-400 transition-transform ${isCategoryDropdownOpen ? "rotate-180" : ""}`}
                />
              </button>

              {/* Dropdown panel */}
              {isCategoryDropdownOpen && (
                <div className="absolute z-50 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg max-h-80 overflow-hidden">
                  {/* Search inside dropdown */}
                  <div className="p-2 border-b border-gray-100">
                    <input
                      type="text"
                      value={categorySearch}
                      onChange={(e) => setCategorySearch(e.target.value)}
                      placeholder="Kategori ara..."
                      className="w-full px-3 py-1.5 border border-gray-200 rounded-md text-sm focus:ring-1 focus:ring-indigo-500 focus:border-indigo-500 outline-none"
                      autoFocus
                    />
                  </div>

                  {/* Category list */}
                  <div className="overflow-y-auto max-h-64">
                    {filteredGroups.length === 0 ? (
                      <div className="px-4 py-3 text-sm text-gray-500 text-center">
                        Kategori bulunamadı
                      </div>
                    ) : (
                      filteredGroups.map((group) => {
                        const subNames = group.sub_categories
                          .map(getSubCategoryName)
                          .filter(Boolean)
                        const allSelected = subNames.every((name) =>
                          selectedCategories.includes(name),
                        )
                        const someSelected =
                          !allSelected &&
                          subNames.some((name) => selectedCategories.includes(name))
                        const isExpanded = expandedGroups.includes(group.main_category)

                        return (
                          <div
                            key={group.main_category}
                            className="border-b border-gray-50 last:border-0"
                          >
                            {/* Main category header */}
                            <div className="flex items-center px-3 py-2 hover:bg-gray-50">
                              <input
                                type="checkbox"
                                checked={allSelected}
                                ref={(input) => {
                                  if (input) {
                                    input.indeterminate = someSelected
                                  }
                                }}
                                onChange={() => toggleMainCategory(group.main_category)}
                                className="w-4 h-4 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mr-2 cursor-pointer"
                              />
                              <button
                                type="button"
                                onClick={() => toggleGroupExpand(group.main_category)}
                                className="flex-1 text-left text-sm font-semibold text-gray-800 flex items-center justify-between"
                              >
                                <span>{group.main_category}</span>
                                <span className="text-xs text-gray-400 font-normal">
                                  {subNames.filter((n) => selectedCategories.includes(n)).length}/
                                  {subNames.length}
                                  <FontAwesomeIcon
                                    icon={faChevronDown}
                                    className={`ml-1.5 w-2.5 h-2.5 transition-transform ${isExpanded ? "rotate-180" : ""}`}
                                  />
                                </span>
                              </button>
                            </div>

                            {/* Sub categories */}
                            {isExpanded && (
                              <div className="pl-6 pb-1">
                                {group.sub_categories.map((sub) => {
                                  const name = getSubCategoryName(sub)
                                  if (!name) { return null }
                                  const isSelected = selectedCategories.includes(name)
                                  return (
                                    <label
                                      key={name}
                                      className="flex items-center px-3 py-1 hover:bg-gray-50 cursor-pointer"
                                    >
                                      <input
                                        type="checkbox"
                                        checked={isSelected}
                                        onChange={() => toggleCategory(name)}
                                        className="w-3.5 h-3.5 text-indigo-600 border-gray-300 rounded focus:ring-indigo-500 mr-2 cursor-pointer"
                                      />
                                      <span className="text-sm text-gray-700">{name}</span>
                                      <span className="ml-auto text-xs text-gray-400">
                                        {sub.count}
                                      </span>
                                    </label>
                                  )
                                })}
                              </div>
                            )}
                          </div>
                        )
                      })
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Platform Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
            <select
              value={platform}
              onChange={(e) => setPlatform(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
            >
              <option value="insta">Instagram</option>
              <option value="insta-reels">Instagram Reels</option>
              <option value="tiktok">TikTok</option>
            </select>
          </div>

          {/* Confidence Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Güven Eşiği: {(confidence * 100).toFixed(0)}%
            </label>
            <input
              type="range"
              min="0.5"
              max="1"
              step="0.05"
              value={confidence}
              onChange={(e) => setConfidence(parseFloat(e.target.value))}
              className="w-full h-2 bg-gray-200 rounded-lg appearance-none cursor-pointer accent-indigo-600"
            />
            <div className="flex justify-between text-xs text-gray-400 mt-1">
              <span>50%</span>
              <span>100%</span>
            </div>
          </div>

          {/* Username Search */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Kullanıcı Adı</label>
            <input
              type="text"
              value={username}
              onChange={(e) => setUsername(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="Kullanıcı adı ara..."
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
            />
          </div>

          {/* Country Input */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Ülke</label>
            <input
              type="text"
              value={country}
              onChange={(e) => setCountry(e.target.value)}
              onKeyDown={handleKeyDown}
              placeholder="ör: Turkey"
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
            />
          </div>

          {/* Gender Select */}
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">Cinsiyet</label>
            <select
              value={gender}
              onChange={(e) => setGender(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-indigo-500 focus:border-indigo-500 outline-none transition"
            >
              <option value="">Tümü</option>
              <option value="female">Kadın</option>
              <option value="male">Erkek</option>
            </select>
          </div>

          {/* Search Button */}
          <div className="flex items-end">
            <button
              onClick={handleSearch}
              disabled={loading}
              className="w-full px-4 py-2 bg-indigo-600 text-white rounded-lg text-sm font-medium hover:bg-indigo-700 focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 transition disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            >
              {loading ? (
                <FontAwesomeIcon icon={faSpinner} spin />
              ) : (
                <FontAwesomeIcon icon={faSearch} />
              )}
              {loading ? "Aranıyor..." : "Ara"}
            </button>
          </div>
        </div>

        {/* Advanced Filters Toggle */}
        <button
          type="button"
          onClick={() => setShowAdvanced(!showAdvanced)}
          className="mt-4 text-sm text-indigo-600 hover:text-indigo-800 font-medium flex items-center gap-1.5"
        >
          <FontAwesomeIcon icon={faFilter} className="w-3 h-3" />
          Gelişmiş Filtreler
          <FontAwesomeIcon
            icon={showAdvanced ? faChevronUp : faChevronDown}
            className="w-2.5 h-2.5"
          />
        </button>

        {/* Advanced Filters */}
        {showAdvanced && (
          <div className="mt-4 pt-4 border-t border-gray-100">
            <div className="grid grid-cols-1 md:grid-cols-3 lg:grid-cols-5 gap-4">
              <RangeInput
                label="Yaş"
                minVal={minAge}
                maxVal={maxAge}
                setMin={setMinAge}
                setMax={setMaxAge}
              />
              <RangeInput
                label="Takipçi"
                minVal={minFollowers}
                maxVal={maxFollowers}
                setMin={setMinFollowers}
                setMax={setMaxFollowers}
              />
              <RangeInput
                label="Etkileşim Oranı"
                minVal={minEngagement}
                maxVal={maxEngagement}
                setMin={setMinEngagement}
                setMax={setMaxEngagement}
              />
              <RangeInput
                label="Ort. Beğeni"
                minVal={minAvgLike}
                maxVal={maxAvgLike}
                setMin={setMinAvgLike}
                setMax={setMaxAvgLike}
              />
              <RangeInput
                label="Ort. Görüntülenme"
                minVal={minAvgView}
                maxVal={maxAvgView}
                setMin={setMinAvgView}
                setMax={setMaxAvgView}
              />
            </div>
          </div>
        )}
      </div>

      {/* Error */}
      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 rounded-lg mb-4">
          {error}
        </div>
      )}

      {/* Results */}
      {searched && !loading && records.length === 0 && !error && (
        <div className="bg-yellow-50 border border-yellow-200 text-yellow-700 px-4 py-3 rounded-lg mb-4">
          Arama kriterlerine uygun kullanıcı bulunamadı.
        </div>
      )}

      {/* Stats Bar */}
      {searched && totalRecords > 0 && (
        <div className="flex flex-wrap items-center gap-3 mb-4 text-sm text-gray-600">
          <span className="bg-indigo-50 text-indigo-700 px-3 py-1 rounded-full font-medium">
            Toplam: {totalRecords.toLocaleString("tr-TR")} kullanıcı
          </span>
          <span className="bg-gray-50 text-gray-600 px-3 py-1 rounded-full">
            Platform:{" "}
            {platform === "insta"
              ? "Instagram"
              : platform === "insta-reels"
                ? "Instagram Reels"
                : "TikTok"}
          </span>
          <span className="bg-gray-50 text-gray-600 px-3 py-1 rounded-full">
            Güven Eşiği: %{(confidence * 100).toFixed(0)}
          </span>
          <span className="bg-gray-50 text-gray-600 px-3 py-1 rounded-full">
            Seçili Kategori: {selectedCategories.length}
          </span>
        </div>
      )}

      {/* Table */}
      {records.length > 0 && (
        <StickyDataTable
          records={records}
          columns={columns}
          totalRecords={totalRecords}
          recordsPerPage={pageSize}
          page={page}
          onPageChange={(p) => setPage(p)}
          recordsPerPageOptions={PAGE_SIZES}
          onRecordsPerPageChange={(size) => {
            setPageSize(size)
            setPage(1)
          }}
          sortStatus={{ columnAccessor: sortByColumn, direction: sortOrder ? "asc" : "desc" }}
          onSortStatusChange={(status) => {
            setSortByColumn(status.columnAccessor)
            setSortOrder(status.direction === "asc")
          }}
          paginationText={({ from, to, totalRecords }) =>
            `${from} - ${to} arası gösteriliyor. Toplam ${totalRecords} kayıt`
          }
        />
      )}

      {/* Profile Detail Modal */}
      {isOpenDetailModal && selectedUser && (
        <ProfileDetailModal
          isOpen={isOpenDetailModal}
          onClose={() => {
            setIsOpenDetailModal(false)
            setSelectedUser(null)
          }}
          data={selectedUser}
        />
      )}
    </div>
  )
}

export default UsersByCategories
